/**
 * Upload de mídia via XHR (docs/06 §3.2).
 *
 * É XHR e não `fetch` para ter `upload.onprogress` determinístico e poder
 * cancelar um envio em andamento pelo `clientMessageId`.
 */
import { mediaInfo, type MediaInfo } from "@zapir/shared";
import { authHeaders } from "./api";
import { parseApiError, ZapirApiError } from "./errors";
import { sanitizeDurationMs } from "./waveform";
import type { UploadableKind } from "./media-kinds";

export interface UploadProgress {
  loaded: number;
  total: number;
}

const uploadControllers = new Map<string, XMLHttpRequest>();

/** Aborta um upload em andamento (botão cancelar da prévia/bolha). */
export function cancelUpload(clientMessageId: string): void {
  uploadControllers.get(clientMessageId)?.abort();
}

export function uploadMedia(args: {
  clientMessageId: string;
  file: Blob;
  fileName: string;
  conversationId: string;
  /** Kind escolhido no menu. Sem ele o servidor deduz pelo MIME — e ai
   *  figurinha vira foto e arquivo de audio vira mensagem de voz. */
  kind?: UploadableKind;
  /** Duração do áudio em ms (0 < d ≤ 120000); inválida é ignorada. */
  durationMs?: number | null;
  onProgress?: (progress: UploadProgress) => void;
  signal?: AbortSignal;
}): Promise<MediaInfo> {
  const { clientMessageId, file, fileName, conversationId, kind, durationMs, onProgress, signal } = args;
  return new Promise<MediaInfo>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new ZapirApiError("SERVER_ERROR", 0, { aborted: true }));
      return;
    }
    const xhr = new XMLHttpRequest();
    uploadControllers.set(clientMessageId, xhr);

    const done = () => {
      uploadControllers.delete(clientMessageId);
      signal?.removeEventListener("abort", onAbort);
    };
    const onAbort = () => xhr.abort();
    signal?.addEventListener("abort", onAbort);

    xhr.open("POST", "/api/media/uploads");
    const headers = authHeaders();
    for (const [key, value] of Object.entries(headers)) {
      xhr.setRequestHeader(key, value);
    }

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress?.({ loaded: event.loaded, total: event.total });
    };

    xhr.onload = () => {
      done();
      let payload: unknown = null;
      try {
        payload = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch {
        payload = null;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        const parsed = mediaInfo.safeParse((payload as { media?: unknown } | null)?.media ?? payload);
        if (!parsed.success) {
          reject(new ZapirApiError("SERVER_ERROR", xhr.status, { contract: true }));
          return;
        }
        resolve(parsed.data);
        return;
      }
      reject(parseApiError(payload, xhr.status));
    };

    xhr.onerror = () => {
      done();
      reject(new ZapirApiError("SERVER_ERROR", 0, { network: true }));
    };

    xhr.onabort = () => {
      done();
      reject(new ZapirApiError("SERVER_ERROR", 0, { aborted: true }));
    };

    const form = new FormData();
    form.append("file", file, fileName);
    form.append("conversationId", conversationId);
    if (kind) form.append("kind", kind);
    // Duração medida no gravador para o servidor guardar; o web tolera
    // resposta sem duração (o player usa decode/metadata como fallback).
    const validDuration = sanitizeDurationMs(durationMs);
    if (validDuration !== undefined) form.append("durationMs", String(validDuration));
    xhr.send(form);
  });
}
